import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch, FiX } from "react-icons/fi";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  TemplateSearchWrapper,
  TemplateSearchInput,
  TemplateListWrapper,
  TemplateCard,
  TemplateCardName,
  TemplateCardIntro,
  ModalFormField,
  ModalLabel,
  ModalInput,
} from "./NewContractModalStyles";
import { Button } from "../../../Components/Button";
import { useContracts } from "../../../Common/Hooks/useContracts";
import { ContractTemplateSummary } from "../../../Common/Types-DTOs/Contracts";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";

interface NewContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (contractId: string) => void;
}

export const NewContractModal = ({ isOpen, onClose, onCreated }: NewContractModalProps) => {
  const { t } = useTranslation();
  const { listContractTemplates, createContract } = useContracts();
  const [templates, setTemplates] = useState<ContractTemplateSummary[]>([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTemplateId(null);
    setTemplateSearch("");
    setTitle("");

    const load = async () => {
      const result = await listContractTemplates();
      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("contracts.contractsList.newContractModal.notifications.loadTemplatesFailed"))
        );
        return;
      }
      setTemplates(result.value.templates);
    };
    load();
  }, [isOpen, t]);

  const filteredTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((template) => template.name.toLowerCase().includes(query));
  }, [templates, templateSearch]);

  const canSubmit = selectedTemplateId && title.trim();

  const handleSubmit = async () => {
    if (!selectedTemplateId || !canSubmit) return;
    setIsSubmitting(true);
    const result = await createContract({
      templateId: selectedTemplateId,
      title: title.trim(),
    });
    setIsSubmitting(false);

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("contracts.contractsList.newContractModal.notifications.createFailed"))
      );
      return;
    }

    onCreated(result.value.contract.id);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t("contracts.contractsList.newContractModal.title")}</ModalTitle>
          <ModalCloseButton onClick={onClose}>
            <FiX size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <TemplateSearchWrapper>
            <FiSearch className="search-icon" size={16} />
            <TemplateSearchInput
              type="text"
              placeholder={t("contracts.contractsList.newContractModal.searchPlaceholder")}
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
          </TemplateSearchWrapper>

          <TemplateListWrapper>
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                type="button"
                $selected={selectedTemplateId === template.id}
                onClick={() => setSelectedTemplateId(template.id)}
              >
                <TemplateCardName>{template.name}</TemplateCardName>
                <TemplateCardIntro>{template.introText}</TemplateCardIntro>
              </TemplateCard>
            ))}
          </TemplateListWrapper>

          {selectedTemplateId && (
            <ModalFormField>
              <ModalLabel>{t("contracts.contractsList.newContractModal.titleFieldLabel")}</ModalLabel>
              <ModalInput
                placeholder={t("contracts.contractsList.newContractModal.titlePlaceholder")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </ModalFormField>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={isSubmitting}>
            {t("contracts.contractsList.newContractModal.submitButton")}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};
